import { DataType } from "@/components/DataTable/Models";
import { Software } from "@/services/global-services/models";
import { IconTable } from "@codexteam/icons";
import { API, BlockAPI, BlockTool } from "@editorjs/editorjs";
import { BlockToolConstructorOptions, BlockToolData } from "@editorjs/editorjs/types/tools";
import { createRoot } from "react-dom/client";
import { DocumentationTableColumnData } from "../DocumentationTableBlock/DocumentationTableBlockTool";
import { IfcTableBlockComponent } from "./IfcTableBlockComponent";

export interface IfcTableBlockData extends BlockToolData {
    title?: string;
    caption?: string;
    columns: DocumentationTableColumnData[];
    hasSoftwareColumns: boolean;
    hasClassificationColumns: boolean;
}

export interface IfcTableBlockConfig {
    currentLanguage: string;
    software: Software[];
    classifications?: Software[];
}

const DEFAULT_CONFIG: { [key: string]: IfcTableBlockData } = {
    "int-en": {
        title: "",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
        ],
        hasSoftwareColumns: true,
        hasClassificationColumns: true,
    },

    "int-fr": {
        title: "",
        columns: [
            {
                value: "Elément",
                dataType: DataType.STRING,
            },
        ],
        hasSoftwareColumns: true,
        hasClassificationColumns: true,
    },

    "int-de": {
        title: "",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
        ],
        hasSoftwareColumns: true,
        hasClassificationColumns: true,
    },

    "int-nl": {
        title: "",
        columns: [
            {
                value: "Element",
                dataType: DataType.STRING,
            },
        ],
        hasSoftwareColumns: true,
        hasClassificationColumns: true,
    },
};

export class IfcTableBlockTool implements BlockTool {
    public static get toolbox() {
        return {
            title: "IFC Class & Types",
            icon: IconTable,
        };
    }

    api: API;
    block?: BlockAPI;
    config: IfcTableBlockData;
    data: IfcTableBlockData;
    currentData: IfcTableBlockData;
    software: Array<Software>;
    classifications?: Array<Software>;
    CSS: { [key: string]: string };
    nodes: { [key: string]: HTMLElement | null };

    // Use `any` to circumvent an issue with Editor.js when using TypeScript
    constructor(params: BlockToolConstructorOptions<IfcTableBlockData, IfcTableBlockConfig>) {
        const { api, block, config, data } = params;
        if (!config) throw new Error("IfcTableBlockTool configuration missing");

        this.api = api;
        this.block = block;
        this.data = data;
        this.config = DEFAULT_CONFIG[config.currentLanguage]!;
        this.software = config.software;
        this.classifications = config.classifications;

        if (data && Object.keys(data).length > 0) {
            this.data = data;
        } else {
            this.data = JSON.parse(JSON.stringify(DEFAULT_CONFIG[config.currentLanguage]!));
        }

        this.currentData = JSON.parse(JSON.stringify(this.data));

        this.CSS = {
            wrapper: "template-editor-content-block",
        };

        this.nodes = {
            holder: null,
            table: null,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(block: HTMLElement) {
        return {
            title: this.api.sanitizer.clean(this.currentData.title || "", {}),
            columns: this.currentData.columns.map((column) => ({
                ...column,
                value: this.api.sanitizer.clean(column.value || "", {}),
            })),
            caption: this.api.sanitizer.clean(this.currentData.caption || "", {}),
            hasSoftwareColumns: true,
            hasClassificationColumns: true,
        };
    }

    render(): HTMLElement {
        if (!this.nodes.table) {
            const rootNode = document.createElement("div");
            rootNode.setAttribute("class", this.CSS.wrapper);
            this.nodes.table = rootNode;

            const root = createRoot(rootNode);
            root.render(
                <IfcTableBlockComponent
                    data={this.data}
                    defaults={this.config}
                    software={this.software}
                    classifications={this.classifications}
                    onChange={(data) => {
                        this.currentData = data;
                        this.block?.dispatchChange();
                    }}
                />
            );
        }

        if (!this.nodes.holder) {
            const holderNode = document.createElement("div");
            holderNode.setAttribute("class", this.CSS.wrapper);
            holderNode.appendChild(this.nodes.table);
            this.nodes.holder = holderNode;
        }

        return this.nodes.holder;
    }
}
